import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Skull, Drop, Info, Leaf, CaretDown, CaretUp } from '@phosphor-icons/react';
import { API_URL } from '../config/api';

/**
 * DiseasePressureCard — pro-grade disease pressure dashboard tile.
 * Shows Smith-Kerns Dollar Spot + Large Patch (warm-season) scores.
 */
const BAND_COLOR = {
  minimal: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-700 dark:text-emerald-400', dot: 'bg-emerald-500' },
  low:     { bg: 'bg-sky-500/10', border: 'border-sky-500/30', text: 'text-sky-700 dark:text-sky-400', dot: 'bg-sky-500' },
  moderate:{ bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-700 dark:text-amber-400', dot: 'bg-amber-500' },
  high:    { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-700 dark:text-red-400', dot: 'bg-red-500' },
  unknown: { bg: 'bg-muted/30', border: 'border-border', text: 'text-muted-foreground', dot: 'bg-muted-foreground' },
};

export const DiseasePressureCard = ({ grassType }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancel = false;
    axios
      .get(`${API_URL}/api/disease-models/pressure`, { withCredentials: true })
      .then((res) => { if (!cancel) setData(res.data); })
      .catch(() => { if (!cancel) setData({ error: true }); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  if (loading) {
    return (
      <Card className="border border-border/60" data-testid="disease-pressure-card">
        <CardContent className="p-5 text-sm text-muted-foreground">Loading disease pressure…</CardContent>
      </Card>
    );
  }

  if (!data || data.error) {
    return (
      <Card className="border border-border/60" data-testid="disease-pressure-card">
        <CardContent className="p-5">
          <p className="text-sm text-muted-foreground">Set your property location to see disease pressure.</p>
        </CardContent>
      </Card>
    );
  }

  const ds = data.dollar_spot || {};
  const lp = data.large_patch || {};
  const dsBand = BAND_COLOR[ds.band] || BAND_COLOR.unknown;
  const lpBand = BAND_COLOR[lp.band] || BAND_COLOR.unknown;

  const grass = (grassType || data.grass_type || '').toLowerCase();
  const isWarmSeason = /(bermuda|zoysia|st.?aug|centipede|warm)/.test(grass);

  return (
    <Card className="border border-border/60" data-testid="disease-pressure-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
              <Skull weight="fill" size={12} /> Disease pressure today
            </div>
            <h3 className="text-base font-semibold mt-0.5">Is your turf under attack?</h3>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1"
            data-testid="disease-pressure-toggle"
          >
            Details {expanded ? <CaretUp size={10} /> : <CaretDown size={10} />}
          </button>
        </div>

        {/* Dollar Spot */}
        <div className={`rounded-lg border ${dsBand.border} ${dsBand.bg} p-3`} data-testid="dollar-spot-tile">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${dsBand.dot}`}></span>
              <span className="text-xs font-semibold">Dollar Spot</span>
              <span className="text-[10px] text-muted-foreground">(Smith-Kerns)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm font-bold" data-testid="dollar-spot-pct">
                {ds.probability_pct != null ? `${ds.probability_pct}%` : '—'}
              </span>
              <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${dsBand.text} ${dsBand.bg} border ${dsBand.border}`} data-testid="dollar-spot-band">
                {ds.band}
              </span>
            </div>
          </div>
          {expanded && ds.inputs && (
            <div className="mt-2 text-[11px] text-muted-foreground grid grid-cols-2 gap-2">
              <span>5-day temp: <span className="font-mono text-foreground">{ds.inputs.temp_mean_5d_f}°F</span></span>
              <span>5-day RH: <span className="font-mono text-foreground">{ds.inputs.rh_mean_5d}%</span></span>
            </div>
          )}
        </div>

        {/* Large Patch (warm-season only) */}
        {isWarmSeason && (
          <div className={`rounded-lg border ${lpBand.border} ${lpBand.bg} p-3`} data-testid="large-patch-tile">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${lpBand.dot}`}></span>
                <span className="text-xs font-semibold">Large Patch</span>
                <span className="text-[10px] text-muted-foreground">(warm-season)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-bold" data-testid="large-patch-score">{lp.score}/100</span>
                <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${lpBand.text} ${lpBand.bg} border ${lpBand.border}`} data-testid="large-patch-band">
                  {lp.band}
                </span>
              </div>
            </div>
            {expanded && (
              <div className="mt-2 text-[11px] text-muted-foreground grid grid-cols-3 gap-2">
                <span>2" soil: <span className="font-mono text-foreground">{lp.soil_temp_f}°F</span></span>
                <span>Rain days: <span className="font-mono text-foreground">{lp.rain_days_7d}</span></span>
                <span>Avg RH: <span className="font-mono text-foreground">{lp.rh_mean_7d}%</span></span>
              </div>
            )}
          </div>
        )}

        {/* Recommendations */}
        {data.recommendations?.length > 0 && (
          <div className="space-y-2 pt-1" data-testid="disease-recommendations">
            {data.recommendations.map((r, i) => (
              <div key={i} className="rounded-md border border-border/50 bg-card p-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <Leaf weight="fill" size={14} className="text-primary" />
                  <span className="text-xs font-semibold">{r.headline}</span>
                </div>
                <ul className="space-y-1 pl-1">
                  {r.actions.map((a, j) => (
                    <li key={j} className="text-[12px] text-muted-foreground flex gap-1.5">
                      <span className="text-primary">›</span>
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        <p className="text-[10px] text-muted-foreground flex items-start gap-1.5 pt-1">
          <Info size={11} className="shrink-0 mt-0.5" />
          <span>
            Models run on your ZIP's live weather via Open-Meteo. Dollar spot uses Smith-Kerns 2013.
            Large Patch uses a soil-temp × rainfall × RH heuristic tuned for Rhizoctonia AG 2-2 LP.
          </span>
        </p>
      </CardContent>
    </Card>
  );
};

export default DiseasePressureCard;
